import config from "lib/config";
import axios from "axios";
import { getDynamicRoute } from "./createProfileIdPath";
import getLogger from "logger/index";
const logger = getLogger("CLIENT_TOKEN");

// CSPM-4003: JWT_SECRET is no longer published to the browser via publicRuntimeConfig.
// Server path: read it from process.env (loaded from Secrets Manager by secretsLoader.js).
// Client path: never has the secret — it calls GET /api/v3/token and the server signs.
const getSecret = () => {
  if (typeof window === "undefined") {
    return process.env.JWT_SECRET;
  }
  return null;
};

// Same path shape the other client API calls use, so it works both on
// realtor.com/realestateagents/* and /realtors/* and behind the CDN.
const getTokenUrl = () => `${config.GATEWAY_URL}/${getDynamicRoute()}/api/v3/token`;

let jwtInstance = null;
const getJwt = () => {
  if (!jwtInstance) {
    jwtInstance = require("jsonwebtoken");
  }
  return jwtInstance;
};

const base64UrlEncode = (str) => {
  if (typeof window === "undefined") {
    return Buffer.from(str, "utf8").toString("base64url");
  }
  const bytes = new TextEncoder().encode(str);
  const binString = Array.from(bytes, (b) => String.fromCharCode(b)).join("");
  return btoa(binString)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
};

const signToken = async (payload, secret) => {
  const header = { alg: "HS256", typ: "JWT" };
  const encodedHeader = base64UrlEncode(JSON.stringify(header));
  const encodedPayload = base64UrlEncode(JSON.stringify(payload));
  const signingInput = `${encodedHeader}.${encodedPayload}`;

  if (typeof window === "undefined") {
    const crypto = require("crypto");
    const signature = crypto
      .createHmac("sha256", secret)
      .update(signingInput)
      .digest("base64url");
    return `${signingInput}.${signature}`;
  }

  const encoder = new TextEncoder();
  const keyData = encoder.encode(secret);
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyData,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const signatureBuffer = await crypto.subtle.sign("HMAC", cryptoKey, encoder.encode(signingInput));
  const signatureArray = Array.from(new Uint8Array(signatureBuffer));
  const signature = btoa(String.fromCharCode(...signatureArray))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
  return `${signingInput}.${signature}`;
};

const generateToken = async () => {
  // Client path: ask the server to sign the token — JWT_SECRET never leaves the server.
  if (typeof window !== "undefined") {
    try {
      const response = await axios.get(getTokenUrl());
      const token = response && response.data && response.data.token;
      if (!token) {
        throw new Error("token endpoint returned no token");
      }
      return token;
    } catch (error) {
      // Surface this clearly: without a token every downstream /api/v3 call gets a 401,
      // which otherwise looks like "all the service calls failed" with no obvious cause.
      logger.error("SRC - UTIL - failed to fetch client token", error);
      throw error;
    }
  }

  // Server path: sign directly with the secret from process.env.
  const secret = getSecret();
  if (!secret) {
    logger.error("SRC - UTIL - JWT_SECRET missing server-side; cannot sign token");
    throw new Error("JWT_SECRET is not configured");
  }
  return signToken(
    {
      exp: Math.floor(Date.now() / 1000) + 300,
      sub: "find_a_realtor",
    },
    secret,
  );
};

/**
 * Verifies a JSON web token and decodes it into its contents.
 * Only valid server-side — JWT_SECRET is not available in the browser.
 */
const decodeToken = (token) => {
  return new Promise((resolve, reject) => {
    const secret = getSecret();
    getJwt().verify(token, secret, (err, decoded) => {
      if (err) {
        logger.info("SRC - UTIL - decode - err", err);
        reject(err);
        return;
      }
      logger.info("SRC - UTIL - decoded", decoded);
      resolve(decoded);
    });
  });
};

module.exports = { generateToken, decodeToken };
